import React from 'react';

const DIMENSION_COLORS = {
    0: '#6366f1', // Operations - indigo
    1: '#ec4899', // Marketing - pink
    2: '#22d3ee', // Sales - cyan
    3: '#10b981', // Finance - emerald
    4: '#f59e0b', // Customer Service - amber
    5: '#8b5cf6', // Data - violet
    6: '#3b82f6', // Communication - blue
    7: '#f97316', // People - orange
};

const DIMENSION_ICONS = ['⚙️', '📣', '💼', '💰', '🎧', '📊', '💬', '👥'];

/**
 * MaturityRadarChart
 * Renders the YAMI 8-dimension breakdown as a clean visual score card.
 * Uses CSS-based bars instead of SVG radar for reliability across browsers.
 */
export default function MaturityRadarChart({ breakdown }) {
    if (!breakdown || !breakdown.dimensions) return null;

    const { yami_score, label, top_recommendations, dimensions, summary } = breakdown;

    const labelColors = {
        Beginner: '#ef4444',
        Emerging: '#f97316',
        Intermediate: '#eab308',
        Advanced: '#22d3ee',
        Elite: '#10b981',
    };
    const scoreColor = labelColors[label] || '#6366f1';

    return (
        <div style={{
            background: 'rgba(15, 20, 40, 0.6)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
        }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                {/* Score circle */}
                <div style={{
                    width: '80px', height: '80px', borderRadius: '50%',
                    background: `conic-gradient(${scoreColor} ${yami_score * 3.6}deg, rgba(255,255,255,0.06) 0deg)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    position: 'relative',
                }}>
                    <div style={{
                        width: '62px', height: '62px', borderRadius: '50%',
                        background: '#0f1428',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                    }}>
                        <span style={{ fontSize: '20px', fontWeight: '800', color: scoreColor, lineHeight: 1 }}>
                            {yami_score}
                        </span>
                        <span style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase' }}>/100</span>
                    </div>
                </div>

                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#f1f5f9' }}>
                            YAMI Score
                        </h3>
                        <span style={{
                            background: scoreColor + '20',
                            color: scoreColor,
                            border: `1px solid ${scoreColor}50`,
                            fontSize: '11px',
                            fontWeight: '700',
                            padding: '2px 10px',
                            borderRadius: '100px',
                        }}>
                            {label}
                        </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', lineHeight: '1.4' }}>
                        Yukti Automation Maturity Index — 8 dimensions
                    </p>
                    {summary && (
                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
                            {summary}
                        </p>
                    )}
                </div>
            </div>

            {/* Dimension bars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                {dimensions.map((dim, idx) => (
                    <div key={dim.name}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: '4px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '13px' }}>{DIMENSION_ICONS[idx]}</span>
                                <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '500' }}>
                                    {dim.name}
                                </span>
                                {dim.tools_found.length > 0 && (
                                    <span style={{
                                        fontSize: '10px', color: '#64748b',
                                        background: 'rgba(255,255,255,0.04)',
                                        padding: '1px 6px', borderRadius: '4px',
                                    }}>
                                        {dim.tools_found.slice(0, 2).join(', ')}
                                        {dim.tools_found.length > 2 ? ` +${dim.tools_found.length - 2}` : ''}
                                    </span>
                                )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                    fontSize: '10px',
                                    color: dim.score >= 65 ? DIMENSION_COLORS[idx] : '#64748b',
                                    fontWeight: dim.score >= 65 ? '600' : '400',
                                }}>
                                    {dim.label}
                                </span>
                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#f1f5f9', width: '32px', textAlign: 'right' }}>
                                    {dim.score}
                                </span>
                            </div>
                        </div>
                        {/* Progress bar */}
                        <div style={{
                            height: '6px', borderRadius: '100px',
                            background: 'rgba(255,255,255,0.06)',
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                height: '100%',
                                width: `${dim.score}%`,
                                background: dim.score >= 65
                                    ? `linear-gradient(90deg, ${DIMENSION_COLORS[idx]}99, ${DIMENSION_COLORS[idx]})`
                                    : dim.score >= 30
                                        ? `linear-gradient(90deg, #eab30899, #eab308)`
                                        : 'rgba(255,255,255,0.1)',
                                borderRadius: '100px',
                                transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                            }} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Top recommendations */}
            {top_recommendations && top_recommendations.length > 0 && (
                <div style={{
                    padding: '14px 16px',
                    background: 'rgba(99, 102, 241, 0.06)',
                    border: '1px solid rgba(99, 102, 241, 0.15)',
                    borderRadius: '10px',
                }}>
                    <div style={{
                        fontSize: '11px', fontWeight: '700', color: '#6366f1',
                        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
                    }}>
                        🎯 Top Opportunities
                    </div>
                    {top_recommendations.slice(0, 3).map((rec, idx) => (
                        <div key={idx} style={{
                            display: 'flex', alignItems: 'flex-start', gap: '6px',
                            marginBottom: idx < top_recommendations.length - 1 ? '6px' : 0,
                            fontSize: '12px', color: '#94a3b8',
                        }}>
                            <span style={{ color: '#6366f1', fontWeight: '700', flexShrink: 0 }}>{idx + 1}.</span>
                            <span>{rec}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
